<script setup>
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import ModalBase from "@/components/modal/ModalBase.vue";
import ModalTabContent from "@/components/modal/ModalTabContent.vue";
import TabsBootstrap from "@/components/TabsBootstrap.vue";
import ArgonCalendar from "@/components/ArgonCalendar.vue";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonRadio from "@/components/ArgonRadio.vue";
import ArgonAddressNormalize from "@/components/ArgonAddressNormalize.vue";
import ArgonLoadingButton from "@/components/ArgonLoadingButton.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import AutoComplete from "@/components/auto-complete/AutoComplete.vue";
import SectionCard from "@/components/SectionCard.vue";
import LinkButton from "@/components/LinkButton.vue";
import SimpleTable from "@/components/table/SimpleTable.vue";
import ArgonTd from "@/components/table/ArgonTd.vue";
import EditableRowActions from "@/components/table/EditableRowActions.vue";
import EditableGridAddRow from "@/components/table/EditableGridAddRow.vue";
import { useEditableRows } from "@/composables/useEditableRows";
import { warehouseStore } from "@/_store/warehouseStore";
import { stopStore } from "@/_store/stopStore";
import { accountStore } from "@/_store/accountStore";
import { dateUtils, errorUtils } from "@/utils";
import { HANDLING_TYPE_LABELS, formatCourier, formatHandlingType } from "@/utils/formatters";
import { courierList } from "@/maestros/courierValues";

const props = defineProps({
  stopId: {
    type: String,
    default: null,
  },
  duplicateFromStopId: {
    type: String,
    default: null,
  },
  defaultDate: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(["close", "success"]);

const modalCreateManualStop = ref(null);
const vuexStore = useStore();
const bootstrap = vuexStore.state.bootstrap;
const warehousesStore = warehouseStore();
const stopsStore = stopStore();
const accountsStore = accountStore();

const isEdit = computed(() => !!props.stopId);
const isDuplicating = computed(() => !!props.duplicateFromStopId);
const stopNumber = ref("");
const modalTitle = computed(() =>
  isEdit.value ? `Modificar ${stopNumber.value}` : "Nueva"
);

const snackbar = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);
const warehouseOptions = ref([]);
const warehouseSearchText = ref("");
const WAREHOUSE_SUGGESTIONS_LIMIT = 10;

const warehouseSuggestions = computed(() => {
  const term = warehouseSearchText.value.trim().toLocaleLowerCase("es-ES");
  if (!term) return warehouseOptions.value.slice(0, WAREHOUSE_SUGGESTIONS_LIMIT);
  return warehouseOptions.value.filter((warehouse) =>
    (warehouse.name || "").toLocaleLowerCase("es-ES").includes(term)
  );
});
const addressError = ref(false);
const currentTab = ref("direccion");
const detailTabs = [
  { label: "Dirección", value: "direccion" },
  { label: "Participantes", value: "participantes" },
  { label: "Bultos", value: "tareas" },
];
const tabContentHeight = 260;
const tasksTableHeight = tabContentHeight;

const createEmptyAddressNormalized = () => ({
  address: "",
  label: "",
  coordenates: null,
  normalized: false,
  postal_code: "",
});

const createDefaultForm = () => ({
  type: "RECOGIDA",
  date: props.defaultDate || dateUtils.formatDate(new Date(), "YYYY-MM-DD"),
  shipper_info: {
    name: "",
    vat_number: "",
    address: "",
  },
  contact: {
    name: "",
    attention_of: "",
    phone: "",
    email: "",
  },
  addressMode: "warehouse",
  warehouse_id: "",
  address_normalized: createEmptyAddressNormalized(),
  time_window_1: {
    start_time: "",
    end_time: "",
  },
  note_1: "",
});

const form = ref(createDefaultForm());

const HANDLING_TYPE_OPTIONS = Object.entries(HANDLING_TYPE_LABELS).map(
  ([value, label]) => ({ value, label })
);

const createEmptyTask = () => ({
  barcode: "",
  weight: 1,
  length: 10,
  width: 10,
  height: 10,
  handling_type: "ESTANDAR",
  content_description: "",
  courier: "PROPIO",
  note_1: "",
  note_2: "",
});

function formatMeasurements(task) {
  const length = Number(task?.length) || 0;
  const width = Number(task?.width) || 0;
  const height = Number(task?.height) || 0;
  if (!length && !width && !height) return "-";
  return `${length.toFixed(2)}x${width.toFixed(2)}x${height.toFixed(2)}`;
}

function parseMeasurements(measurements) {
  const parts = String(measurements || "").split("x").map((part) => Number(part));
  return {
    length: Number.isFinite(parts[0]) ? parts[0] : 0,
    width: Number.isFinite(parts[1]) ? parts[1] : 0,
    height: Number.isFinite(parts[2]) ? parts[2] : 0,
  };
}

function buildOriginNotes(originStop) {
  const contactName = originStop?.contact?.name || "";
  const address =
    originStop?.address_normalized?.address || originStop?.address?.street || "";
  return {
    note_1: contactName ? `Cliente Origen: ${contactName}` : "",
    note_2: address,
  };
}

function mapStopTasksToRows(sourceTasks, originStop = null) {
  const originNotes = originStop ? buildOriginNotes(originStop) : null;
  return (Array.isArray(sourceTasks) ? sourceTasks : []).map((task) => {
    const { length, width, height } = parseMeasurements(task?.measurements);
    return {
      barcode: task?.barcode || "",
      weight: task?.weight || 0,
      length,
      width,
      height,
      handling_type: task?.handling_type || "ESTANDAR",
      content_description: task?.content_description || "",
      courier: task?.courier || "PROPIO",
      note_1: originNotes?.note_1 ?? task?.note_1 ?? "",
      note_2: originNotes?.note_2 ?? task?.note_2 ?? "",
    };
  });
}

function fillFormFromStop(stop) {
  const hasWarehouse = !!stop?.warehouse_id;
  form.value = {
    type: stop?.type || "RECOGIDA",
    date: String(stop?.date || "").slice(0, 10),
    shipper_info: {
      name: stop?.shipper_info?.name || "",
      vat_number: stop?.shipper_info?.vat_number || "",
      address: stop?.shipper_info?.address?.street || "",
    },
    contact: {
      name: stop?.contact?.name || "",
      attention_of: stop?.contact?.attention_of || "",
      phone: stop?.contact?.phone || "",
      email: stop?.contact?.email || "",
    },
    addressMode: hasWarehouse ? "warehouse" : "free",
    warehouse_id: stop?.warehouse_id || "",
    address_normalized: hasWarehouse
      ? createEmptyAddressNormalized()
      : {
          address: stop?.address_normalized?.address || "",
          label: stop?.address_normalized?.label || "",
          coordenates: stop?.address_normalized?.coordenates || null,
          normalized: !!stop?.address_normalized?.normalized,
          postal_code: stop?.address_normalized?.postal_code || "",
        },
    time_window_1: {
      start_time: stop?.time_window_1?.start_time || "",
      end_time: stop?.time_window_1?.end_time || "",
    },
    note_1: stop?.note_1 || "",
  };
  warehouseSearchText.value = hasWarehouse
    ? warehouseOptions.value.find((warehouse) => warehouse._id === stop.warehouse_id)?.name || ""
    : "";

  tasks.value = mapStopTasksToRows(stop?.tasks);
}

function fillFormFromDuplicateSource(stop) {
  form.value = {
    type: "ENTREGA",
    date: String(stop?.date || "").slice(0, 10),
    shipper_info: {
      name: stop?.shipper_info?.name || "",
      vat_number: stop?.shipper_info?.vat_number || "",
      address: stop?.shipper_info?.address?.street || "",
    },
    contact: {
      name: "",
      attention_of: "",
      phone: "",
      email: "",
    },
    addressMode: "warehouse",
    warehouse_id: "",
    address_normalized: createEmptyAddressNormalized(),
    time_window_1: {
      start_time: "",
      end_time: "",
    },
    note_1: "",
  };
  warehouseSearchText.value = "";

  tasks.value = mapStopTasksToRows(stop?.tasks, stop);
}

const tasks = ref([]);
const editState = useEditableRows();
const saveErrors = ref({
  weight: false,
  length: false,
  width: false,
  height: false,
});

function resetSaveErrors() {
  saveErrors.value = {
    weight: false,
    length: false,
    width: false,
    height: false,
  };
}

const rules = {
  type: {
    required: helpers.withMessage("El tipo es obligatorio", required),
  },
  date: {
    required: helpers.withMessage("La fecha es obligatoria", required),
  },
  contact: {
    name: {
      required: helpers.withMessage("El nombre de contacto es obligatorio", required),
    },
  },
};

const v$ = useVuelidate(rules, form);

const contactCardTitle = computed(() =>
  form.value.type === "ENTREGA" ? "Destinatario" : "Remitente"
);

const taskHeaders = Object.freeze([
  { header: "Kg", field: "weight", position: "start", width: "task-col-weight" },
  { header: "Medidas", field: "measurements", position: "start", width: "task-col-measurements" },
  { header: "Manipulación", field: "handling_type", position: "start", width: "task-col-handling" },
  { header: "Naturaleza", field: "content_description", position: "start", width: "task-col-description" },
  { header: "Transportista", field: "courier", position: "start", width: "task-col-courier" },
  {
    header: "Código barras",
    field: "barcode",
    position: "start",
    width: "task-col-barcode",
    tooltip: "Si no indicas código de barras, se genera uno automáticamente.",
  },
  { header: "", field: "actions", position: "center", width: "task-col-actions" },
]);

const tasksRows = computed(() => ({ total: tasks.value.length, data: tasks.value }));

function getPosition(field) {
  const column = taskHeaders.find((col) => col.field === field);
  return column?.position || "start";
}

function closeSnackbar() {
  snackbar.value = null;
}

function showError(message) {
  snackbar.value = {
    title: "Error",
    message: message || "No se pudo crear la parada",
    type: "danger",
    color: "white",
  };
}

function validateTaskRow(item) {
  const errors = {
    weight: !(Number(item.weight) > 0),
    length: !(Number(item.length) > 0),
    width: !(Number(item.width) > 0),
    height: !(Number(item.height) > 0),
  };
  saveErrors.value = errors;
  return !Object.values(errors).some(Boolean);
}

const newRowFirstInputRef = ref(null);
const addTaskRowRef = ref(null);
const tasksTableRef = ref(null);

function addTaskRow() {
  if (editState.isEditingAny()) return;
  const row = editState.createNewRow(createEmptyTask);
  tasks.value.push(row);
  resetSaveErrors();
  editState.startEdit(row, { focusRef: newRowFirstInputRef, scrollRef: tasksTableRef });
}

function editTaskRow(item) {
  if (editState.isEditingAny()) return;
  resetSaveErrors();
  editState.startEdit(item, { focusRef: newRowFirstInputRef });
}

function saveTaskRow(item) {
  editState.confirmEdit(item, { validate: validateTaskRow, focusRef: addTaskRowRef });
}

function cancelTaskRow(item) {
  editState.cancelEdit(item, {
    onDiscardNew: () => removeTask(item),
  });
}

function removeTask(task) {
  tasks.value = tasks.value.filter((item) => item !== task);
}

function onTaskRowEnter(item) {
  if (item) {
    if (editState.isEditing(item)) saveTaskRow(item);
    return;
  }
  if (!editState.isEditingAny()) addTaskRow();
}

function onWarehouseTextChanged(value) {
  warehouseSearchText.value = value;
  form.value.warehouse_id = "";
}

function onWarehouseSelected(item) {
  form.value.warehouse_id = item._id;
  warehouseSearchText.value = item.name;
}

function syncWarehouseDisplayName() {
  if (form.value.addressMode !== "warehouse" || !form.value.warehouse_id) return;
  const match = warehouseOptions.value.find(
    (warehouse) => warehouse._id === form.value.warehouse_id
  );
  if (match) warehouseSearchText.value = match.name;
}

function copyShipperToContact() {
  form.value.contact.name = form.value.shipper_info.name;
}

function copyAccountToShipper() {
  const account = accountsStore.account;
  if (!account) return;
  form.value.shipper_info.name = account.name || "";
  form.value.shipper_info.vat_number = account.vat_number || "";
  form.value.shipper_info.address = account.address || "";
}

function validateAddressSelection() {
  if (form.value.addressMode === "warehouse") {
    if (!form.value.warehouse_id) {
      return "El punto habitual es obligatorio";
    }
    return "";
  }

  const address = form.value.address_normalized;
  if (!address.address) {
    return "La dirección es obligatoria";
  }
  if (!address.normalized) {
    return "Debes validar la dirección antes de guardar";
  }
  return "";
}

function handleHide() {
  form.value = createDefaultForm();
  tasks.value = [];
  resetSaveErrors();
  isSaving.value = false;
  isLoading.value = false;
  addressError.value = false;
  stopNumber.value = "";
  warehouseSearchText.value = "";
  stopsStore.stop = null;
  v$.value.$reset();
  emit("close");
}

async function confirmCreate() {
  if (isSaving.value || isLoading.value) return;

  await v$.value.$validate();

  if (v$.value.type.$error) {
    showError(v$.value.type.$errors[0].$message);
    return;
  }
  if (v$.value.date.$error) {
    showError(v$.value.date.$errors[0].$message);
    return;
  }

  const addressErrorMessage = validateAddressSelection();
  addressError.value = !!addressErrorMessage;
  if (addressErrorMessage) {
    currentTab.value = "direccion";
    showError(addressErrorMessage);
    return;
  }

  if (v$.value.contact.name.$error) {
    currentTab.value = "participantes";
    showError(v$.value.contact.name.$errors[0].$message);
    return;
  }

  if (tasks.value.length === 0) {
    currentTab.value = "tareas";
    showError("Añade al menos un bulto");
    return;
  }

  if (editState.isEditingAny()) {
    currentTab.value = "tareas";
    showError("Confirma o cancela el bulto que estás editando antes de guardar");
    return;
  }

  isSaving.value = true;
  closeSnackbar();

  const payload = {
    type: form.value.type,
    date: form.value.date,
    contact: {
      name: form.value.contact.name.trim(),
      attention_of: form.value.contact.attention_of.trim(),
      phone: form.value.contact.phone.trim(),
      email: form.value.contact.email.trim(),
    },
    time_window_1: { ...form.value.time_window_1 },
    note_1: form.value.note_1.trim(),
    tasks: tasks.value.map((task) => ({
      barcode: task.barcode,
      weight: task.weight,
      length: task.length,
      width: task.width,
      height: task.height,
      handling_type: task.handling_type,
      content_description: task.content_description,
      courier: task.courier || "PROPIO",
      note_1: task.note_1 || "",
      note_2: task.note_2 || "",
    })),
  };

  const shipperName = form.value.shipper_info.name.trim();
  const shipperVatNumber = form.value.shipper_info.vat_number.trim();
  const shipperAddress = form.value.shipper_info.address.trim();
  if (isEdit.value || shipperName || shipperVatNumber || shipperAddress) {
    payload.shipper_info = {
      name: shipperName,
      vat_number: shipperVatNumber,
      address: { street: shipperAddress },
    };
  }

  if (form.value.addressMode === "warehouse") {
    payload.warehouse_id = form.value.warehouse_id;
  } else {
    payload.address_normalized = { ...form.value.address_normalized };
  }

  try {
    if (isEdit.value) {
      await stopsStore.updateManualStop(props.stopId, payload);
    } else {
      await stopsStore.createManualStop(payload);
    }
    modalCreateManualStop.value?.closeModal();
    emit("success");
  } catch (error) {
    showError(
      errorUtils.getErrorMessageHtml(
        error,
        isEdit.value ? "No se pudo modificar la parada" : "No se pudo crear la parada"
      )
    );
  } finally {
    isSaving.value = false;
  }
}

function loadStopDetail(stopId) {
  isLoading.value = true;
  closeSnackbar();
  stopsStore.getStopById(stopId);
}

onMounted(() => {
  const modal = new bootstrap.Modal(
    document.getElementById("modalCreateManualStop"),
    { keyboard: true, backdrop: "static" }
  );
  modal.show();
  warehousesStore
    .getWarehouses({ page: 1, limit: 500 }, {}, { _id: -1 });
  if (!accountsStore.account) {
    accountsStore.getAccount().catch(() => null);
  }

  if (isEdit.value) {
    loadStopDetail(props.stopId);
  } else if (isDuplicating.value) {
    loadStopDetail(props.duplicateFromStopId);
  }
});

watch(
  () => stopsStore.stop,
  (value) => {
    if (!isLoading.value || !value) return;
    if (isDuplicating.value) {
      fillFormFromDuplicateSource(value);
      isLoading.value = false;
      return;
    }
    stopNumber.value = value?.number || "";
    fillFormFromStop(value);
    isLoading.value = false;
  }
);

watch(
  () => stopsStore.error,
  (value) => {
    if (!value || !isLoading.value) return;
    showError(errorUtils.getErrorMessageHtml(value, "No se pudo cargar la parada"));
    isLoading.value = false;
  }
);

watch(
  () => warehousesStore.warehouses,
  (value) => {
    warehouseOptions.value = Array.isArray(value?.data) ? value.data : [];
    syncWarehouseDisplayName();
  }
);

watch(currentTab, (tab) => {
  if (tab !== "tareas") return;
  if (editState.isEditingAny()) cancelTaskRow(editState.editingRow.value);
  nextTick(() => addTaskRowRef.value?.focus());
});
</script>

<template>
  <modal-base
    id="modalCreateManualStop"
    ref="modalCreateManualStop"
    width="56rem"
    @hide="handleHide"
  >
    <template #header>
      <h6 class="mb-0">{{ modalTitle }}</h6>
    </template>

    <template #body>
      <div class="modal-form--wide-labels">
        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label class="col-form-label required">Tipo</label>
          </div>
          <div class="col">
            <div class="d-flex gap-4">
              <argon-radio
                id="manual-stop-type-recogida"
                v-model="form.type"
                name="RECOGIDA"
                group-name="manual-stop-type"
                :disabled="isEdit || isDuplicating"
              >
                Recogida
              </argon-radio>
              <argon-radio
                id="manual-stop-type-entrega"
                v-model="form.type"
                name="ENTREGA"
                group-name="manual-stop-type"
                :disabled="isEdit || isDuplicating"
              >
                Entrega
              </argon-radio>
            </div>
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label for="manual-stop-date" class="col-form-label required">Fecha</label>
          </div>
          <div class="col">
            <argon-calendar
              id="manual-stop-date"
              v-model="form.date"
              :error="v$.date.$error"
            />
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label class="col-form-label">Horario</label>
          </div>
          <div class="col">
            <div class="row g-2">
              <div class="col-6">
                <argon-input
                  id="manual-stop-time-window-1-start"
                  v-model="form.time_window_1.start_time"
                  type="time"
                />
              </div>
              <div class="col-6">
                <argon-input
                  id="manual-stop-time-window-1-end"
                  v-model="form.time_window_1.end_time"
                  type="time"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label for="manual-stop-note" class="col-form-label">Observaciones</label>
          </div>
          <div class="col">
            <argon-input id="manual-stop-note" v-model="form.note_1" :max-length="60" />
          </div>
        </div>

        <tabs-bootstrap
          v-model="currentTab"
          :tabs="detailTabs"
          size="sm"
          refined
          style-nav="nav-underline"
        />

        <modal-tab-content :height="`${tabContentHeight}px`">
        <div v-if="currentTab === 'participantes'" class="row">
          <div class="col-6">
            <section-card title="Cargador">
              <template #actions>
                <link-button
                  label="Copiar datos de la cuenta"
                  icon="fa-copy"
                  :disabled="!accountsStore.account"
                  @click="copyAccountToShipper"
                />
              </template>

              <div class="row modal-form-row modal-form-row--compact-top">
                <div class="modal-form-label">
                  <label for="manual-stop-shipper-name" class="col-form-label">Razón social</label>
                </div>
                <div class="col">
                  <argon-input
                    id="manual-stop-shipper-name"
                    v-model="form.shipper_info.name"
                    :max-length="60"
                  />
                </div>
              </div>

              <div class="row modal-form-row">
                <div class="modal-form-label">
                  <label for="manual-stop-shipper-vat" class="col-form-label">NIF</label>
                </div>
                <div class="col">
                  <argon-input
                    id="manual-stop-shipper-vat"
                    v-model="form.shipper_info.vat_number"
                    :max-length="25"
                  />
                </div>
              </div>

              <div class="row modal-form-row mb-0">
                <div class="modal-form-label">
                  <label for="manual-stop-shipper-address" class="col-form-label">Domicilio</label>
                </div>
                <div class="col">
                  <argon-input
                    id="manual-stop-shipper-address"
                    v-model="form.shipper_info.address"
                    :max-length="120"
                  />
                </div>
              </div>
            </section-card>
          </div>

          <div class="col-6">
            <section-card :title="contactCardTitle">
              <template #actions>
                <link-button
                  label="Copiar datos del cargador"
                  icon="fa-copy"
                  @click="copyShipperToContact"
                />
              </template>

              <div class="row modal-form-row modal-form-row--compact-top">
                <div class="modal-form-label">
                  <label for="manual-stop-contact-name" class="col-form-label required"
                    >Nombre</label
                  >
                </div>
                <div class="col">
                  <argon-input
                    id="manual-stop-contact-name"
                    v-model="form.contact.name"
                    :error="v$.contact.name.$error"
                    :is-required="true"
                    :max-length="60"
                  />
                </div>
              </div>

              <div class="row modal-form-row">
                <div class="modal-form-label">
                  <label for="manual-stop-contact-attention-of" class="col-form-label"
                    >Contacto</label
                  >
                </div>
                <div class="col">
                  <argon-input
                    id="manual-stop-contact-attention-of"
                    v-model="form.contact.attention_of"
                    :max-length="40"
                  />
                </div>
              </div>

              <div class="row modal-form-row">
                <div class="modal-form-label">
                  <label for="manual-stop-contact-phone" class="col-form-label">Teléfono</label>
                </div>
                <div class="col">
                  <argon-input id="manual-stop-contact-phone" v-model="form.contact.phone" :max-length="15" />
                </div>
              </div>

              <div class="row modal-form-row mb-0">
                <div class="modal-form-label">
                  <label for="manual-stop-contact-email" class="col-form-label">Email</label>
                </div>
                <div class="col">
                  <argon-input
                    id="manual-stop-contact-email"
                    v-model="form.contact.email"
                    type="email"
                    :max-length="75"
                  />
                </div>
              </div>
            </section-card>
          </div>
        </div>

        <div v-else-if="currentTab === 'direccion'">
          <div class="d-flex align-items-center justify-content-center gap-4 mb-2">
            <argon-radio
              id="manual-stop-address-mode-warehouse"
              v-model="form.addressMode"
              name="warehouse"
              group-name="manual-stop-address-mode"
            >
              Punto habitual
            </argon-radio>
            <argon-radio
              id="manual-stop-address-mode-free"
              v-model="form.addressMode"
              name="free"
              group-name="manual-stop-address-mode"
            >
              Dirección nueva
            </argon-radio>
          </div>

          <div class="row modal-form-row--compact-top">
            <div class="col">
              <auto-complete
                v-if="form.addressMode === 'warehouse'"
                id="manual-stop-warehouse"
                v-model="warehouseSearchText"
                type="text"
                :error="addressError"
                :data-suggestions="warehouseSuggestions"
                :item-text="['name']"
                menu-width="300"
                @text-changed="onWarehouseTextChanged"
                @selected-change="onWarehouseSelected"
              />
              <argon-address-normalize
                v-else
                id="manual-stop-address"
                v-model="form.address_normalized"
                :error="addressError"
                label-input-id="manual-stop-address-label"
                :label-max-length="180"
              />
            </div>
          </div>
        </div>

        <div v-else-if="currentTab === 'tareas'">
          <simple-table
            ref="tasksTableRef"
            :headers="taskHeaders"
            :items="tasksRows"
            :empty-message="'Sin bultos añadidos'"
            :show-line-separator="true"
            :height="tasksTableHeight"
            :body-min-height="150"
            fixed-layout
            @row-enter="onTaskRowEnter"
          >
            <template #default="{ item }">
              <argon-td
                v-if="!editState.isEditing(item)"
                :text-primary="String(item.weight)"
                :position="getPosition('weight')"
              />
              <argon-td v-else :position="getPosition('weight')">
                <input
                  ref="newRowFirstInputRef"
                  v-model.number="item.weight"
                  type="number"
                  min="0"
                  step="0.01"
                  class="form-control form-control-sm editable-cell-input"
                  :class="{ 'is-invalid': saveErrors.weight }"
                />
              </argon-td>

              <argon-td
                v-if="!editState.isEditing(item)"
                :text-primary="formatMeasurements(item)"
                :position="getPosition('measurements')"
              />
              <argon-td v-else :position="getPosition('measurements')">
                <div class="d-flex gap-1">
                  <input
                    v-model.number="item.length"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Largo"
                    class="form-control form-control-sm editable-cell-input editable-cell-input--flex"
                    :class="{ 'is-invalid': saveErrors.length }"
                  />
                  <input
                    v-model.number="item.width"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Ancho"
                    class="form-control form-control-sm editable-cell-input editable-cell-input--flex"
                    :class="{ 'is-invalid': saveErrors.width }"
                  />
                  <input
                    v-model.number="item.height"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Alto"
                    class="form-control form-control-sm editable-cell-input editable-cell-input--flex"
                    :class="{ 'is-invalid': saveErrors.height }"
                  />
                </div>
              </argon-td>

              <argon-td
                v-if="!editState.isEditing(item)"
                :text-primary="formatHandlingType(item.handling_type)"
                :position="getPosition('handling_type')"
              />
              <argon-td v-else :position="getPosition('handling_type')">
                <select
                  v-model="item.handling_type"
                  class="form-select form-select-sm editable-cell-input"
                >
                  <option
                    v-for="option in HANDLING_TYPE_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </argon-td>

              <argon-td
                v-if="!editState.isEditing(item)"
                :text-primary="item.content_description || '-'"
                :position="getPosition('content_description')"
                max-width="100%"
              />
              <argon-td v-else :position="getPosition('content_description')">
                <input
                  v-model="item.content_description"
                  type="text"
                  class="form-control form-control-sm editable-cell-input"
                />
              </argon-td>

              <argon-td
                v-if="!editState.isEditing(item)"
                :text-primary="formatCourier(item.courier)"
                :position="getPosition('courier')"
              />
              <argon-td v-else :position="getPosition('courier')">
                <select
                  v-model="item.courier"
                  class="form-select form-select-sm editable-cell-input"
                >
                  <option
                    v-for="option in courierList"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </argon-td>

              <argon-td
                v-if="!editState.isEditing(item)"
                :text-primary="item.barcode || '-'"
                :position="getPosition('barcode')"
                max-width="150px"
              />
              <argon-td v-else :position="getPosition('barcode')">
                <input
                  v-model="item.barcode"
                  type="text"
                  class="form-control form-control-sm editable-cell-input"
                />
              </argon-td>

              <argon-td :position="getPosition('actions')">
                <editable-row-actions
                  :editing="editState.isEditing(item)"
                  :disabled="editState.isEditingAny()"
                  @edit="editTaskRow(item)"
                  @save="saveTaskRow(item)"
                  @cancel="cancelTaskRow(item)"
                  @delete="removeTask(item)"
                />
              </argon-td>
            </template>
            <template #foot-table>
              <editable-grid-add-row
                ref="addTaskRowRef"
                :colspan="taskHeaders.length"
                label="Nuevo bulto"
                :disabled="editState.isEditingAny()"
                @click="addTaskRow"
              />
            </template>
          </simple-table>
        </div>
        </modal-tab-content>
      </div>
    </template>

    <template #footer>
      <argon-loading-button
        class="btn bg-gradient-primary btn-xs"
        color="primary"
        variant="gradient"
        :label="
          isSaving
            ? (isEdit ? 'Guardando...' : 'Creando...')
            : (isEdit ? 'Guardar cambios' : 'Crear parada')
        "
        :loading="isSaving"
        :disable-button="isSaving || isLoading"
        @click="confirmCreate"
      />
    </template>

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </modal-base>
</template>

<style scoped>
:deep(.task-col-barcode) {
  width: 16%;
}

:deep(.task-col-weight) {
  width: 8%;
}

:deep(.task-col-measurements) {
  width: 22%;
}

:deep(.task-col-handling) {
  width: 14%;
}

:deep(.task-col-description) {
  width: 18%;
}

:deep(.task-col-courier) {
  width: 14%;
}

:deep(.task-col-actions) {
  width: 8%;
}
</style>
